import axios from "axios";
import {
  API_PREFIX,
  API_BASE_URL,
  API_ENDPOINTS,
  STORAGE_KEYS,
} from "../constants/config";

const API_URL = `${API_BASE_URL}${API_PREFIX}`;

const getAuthHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem(STORAGE_KEYS.TOKEN)}`,
  "X-Authenticated-User": localStorage.getItem(STORAGE_KEYS.USERNAME),
});

// ─── Session-scoped logo cache (memory only, never localStorage) ─────────────
let _sessionLogoUrl = null;

export const logoCache = {
  get: () => _sessionLogoUrl,
  set: (url) => { _sessionLogoUrl = url; },
  clear: () => { _sessionLogoUrl = null; },
};

const redirectToLogin = () => {
  logoCache.clear();
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.USERNAME);
  localStorage.removeItem("userFullName");
  localStorage.removeItem("companyName");
  window.location.href = "/login";
};

// Returns true ONLY for genuine auth failures that require logout.
// Never matches on business error messages — only HTTP status + specific error codes.
const isAuthError = (error) => {
  const status = error?.response?.status;
  // Only 401 triggers logout — 403 means forbidden (valid token, wrong permission)
  if (status !== 401) return false;
  // For 401, check if it's a JWT/token issue vs a business 401
  const data = error?.response?.data;
  const code = data?.errorCode || data?.code || "";
  const msg = (data?.message || data?.error || data?.errorMessage || "").toLowerCase();
  // Specific JWT error codes from backend
  const authCodes = ["MISSING_TENANT", "INVALID_TENANT", "UNAUTHORIZED"];
  if (authCodes.includes(code)) return true;
  // JWT-specific phrases that only appear in real auth errors
  const jwtPhrases = ["jwt expired", "jwt signature", "malformed jwt", "invalid jwt", "token expired", "token is expired"];
  return jwtPhrases.some((phrase) => msg.includes(phrase));
};

// Request interceptor: always attach latest token from localStorage
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const username = localStorage.getItem(STORAGE_KEYS.USERNAME);
    if (username) {
      config.headers["X-Authenticated-User"] = username;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// Silent token refresh with queue to handle concurrent 401s
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip refresh/public endpoints — let them handle their own errors
    if (
      originalRequest.url?.includes("/auth/refresh") ||
      originalRequest.url?.includes("/registration") ||
      originalRequest.url?.includes("/auth/login")
    ) {
      return Promise.reject(error);
    }

    const status = error.response?.status;

    // 403 = forbidden. Only redirect if it's a tenant/auth 403, not a business 403.
    if (status === 403) {
      const code = error.response?.data?.errorCode || error.response?.data?.code || "";
      if (["INVALID_TENANT", "FORBIDDEN", "ACCESS_DENIED"].includes(code)) {
        processQueue(error, null);
        redirectToLogin();
        return Promise.reject(error);
      }
      // Business 403 — let the caller handle it
      return Promise.reject(error);
    }

    // 401 — attempt silent token refresh once
    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (!refreshToken) {
        redirectToLogin();
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axios(originalRequest);
          })
          .catch(() => {
            redirectToLogin();
            return Promise.reject(error);
          });
      }

      isRefreshing = true;

      try {
        const response = await axios.post(
          `${API_URL}/auth/refresh`,
          { refreshToken },
          { headers: { "Content-Type": "application/json" } },
        );

        const { accessToken, refreshToken: newRefreshToken } = response.data;
        localStorage.setItem(STORAGE_KEYS.TOKEN, accessToken);
        if (newRefreshToken) {
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
        }

        processQueue(null, accessToken);
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Any other error that looks like a JWT/auth issue — redirect
    if (isAuthError(error)) {
      redirectToLogin();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

// Auth Services
export const authService = {
  login: async (userName, password) => {
    const response = await axios.post(`${API_URL}${API_ENDPOINTS.LOGIN}`, {
      userName,
      password,
    });
    return response.data;
  },

  logout: async () => {
    const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    await axios.post(
      `${API_URL}${API_ENDPOINTS.LOGOUT}`,
      { refreshToken },
      {
        headers: getAuthHeaders(),
      },
    );
  },

  signup: async (formData) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.SIGNUP}`,
      formData,
    );
    return response.data;
  },

  addUser: async (formData) => {
    const response = await axios.post(`${API_URL}/signup/users`, formData, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  refreshToken: async (refreshToken) => {
    const response = await axios.post(
      `${API_URL}/auth/refresh`,
      { refreshToken },
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  },
};

// Profile Services
export const profileService = {
  getProfile: async () => {
    const response = await axios.get(`${API_URL}${API_ENDPOINTS.PROFILE}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateProfile: async (userName, changes) => {
    const response = await axios.patch(
      `${API_URL}${API_ENDPOINTS.UPDATE_PROFILE(userName)}`,
      changes,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  updateUser: async (perDId, changes) => {
    const response = await axios.patch(
      `${API_URL}${API_ENDPOINTS.UPDATE_USER(perDId)}`,
      changes,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  adminUpdateUser: async (perDId, data) => {
    const response = await axios.put(
      `${API_URL}/admin/users/${perDId}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  adminDeleteUser: async (perDId) => {
    const response = await axios.delete(
      `${API_URL}/admin/users/${perDId}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  adminGetAllUsers: async () => {
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.ADMIN_ALL_USERS}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  adminGetRoles: async () => {
    const response = await axios.get(
      `${API_URL}/admin/users/roles`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  searchUsers: async (searchBody) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.SEARCH_USERS}`,
      searchBody,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  uploadClientLogo: async (file) => {
    const formData = new FormData();
    formData.append("logo", file);
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.UPLOAD_CLIENT_LOGO}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem(STORAGE_KEYS.TOKEN)}`,
          "X-Authenticated-User": localStorage.getItem(STORAGE_KEYS.USERNAME),
        },
      },
    );
    return response.data;
  },

  deleteClientLogo: async () => {
    const response = await axios.delete(
      `${API_URL}${API_ENDPOINTS.UPLOAD_CLIENT_LOGO}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  getClientLogo: async () => {
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.GET_CLIENT_LOGO}`,
      { headers: getAuthHeaders(), responseType: "blob" },
    );
    return response.data;
  },
};

// Stock Services
export const stockService = {
  // ── Product ──────────────────────────────────────────────────────────────────
  createProduct: async (productData) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.CREATE_PRODUCT}`,
      productData,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  findProductByName: async (name, productType, manufacturer) => {
  try {
    const url = `${API_URL}${API_ENDPOINTS.SEARCH_PRODUCT_BY_NAME(name, productType, manufacturer)}`;

    const response = await axios.get(url, {
      headers: getAuthHeaders(),
    });

    return response.data || null;
  } catch (err) {

    if (err.response?.status === 404) return null;
    throw err;
  }
},

  // ── Stock ─────────────────────────────────────────────────────────────────────
  createStock: async (stockData) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.CREATE_STOCK}`,
      stockData,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  searchStock: async (
    name,
    manufacturer,
    billNo,
    distributorName,
    distributorMobile,
    page,
    size,
  ) => {
    const body = {};
    if (name && name.trim()) body.name = name.trim();
    if (manufacturer && manufacturer.trim())
      body.manufacturer = manufacturer.trim();
    if (billNo && billNo.trim()) body.billNo = billNo.trim();
    if (distributorName && distributorName.trim())
      body.distributorName = distributorName.trim();
    if (distributorMobile && distributorMobile.trim())
      body.distributorMobile = distributorMobile.trim();
    if (page !== undefined && size !== undefined) {
      body.page = page;
      body.size = size;
      body.sortBy = "expiryDate";
      body.sortDirection = "asc";
    }

    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.SEARCH_STOCK}`,
      body,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  checkStockAvailability: async (name, searchBy = "name") => {
    const response = await axios.get(
      `${API_URL}/stock/availability?query=${encodeURIComponent(name.trim())}&searchBy=${searchBy}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  releaseStockReservation: async (clientId, reservationId, reservedStocks) => {
    await axios.post(
      `${API_URL}${API_ENDPOINTS.STOCK_RELEASE}`,
      { clientId, reservationId, items: reservedStocks },
      { headers: getAuthHeaders() },
    );
  },

  getAllCategories: async () => {
    const response = await axios.get(`${API_URL}/stock/categories`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getCategoryByCode: async (code) => {
    const response = await axios.get(
      `${API_URL}/stock/categories/${encodeURIComponent(code)}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  createCategory: async (data) => {
    const response = await axios.post(`${API_URL}/stock/categories`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateCategory: async (data) => {
    const response = await axios.patch(`${API_URL}/stock/categories`, data, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateStock: async (stockUpdates) => {
    const response = await axios.patch(
      `${API_URL}${API_ENDPOINTS.UPDATE_STOCK}`,
      stockUpdates,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  deleteStock: async (stockId) => {
    const response = await axios.delete(
      `${API_URL}${API_ENDPOINTS.DELETE_STOCK(stockId)}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  getStockHistory: async (stockId) => {
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.STOCK_HISTORY(stockId)}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },
  //this method is not yet implemented in backend side
  getProductType: async () => {
    const res = await axios.get("/api/product-types");
    return res.data;
  },

  // Client Field Config API
  getClientFieldConfig: async () => {
    try {
      const response = await axios.get(
        `${API_URL}${API_ENDPOINTS.CLIENT_FIELD_CONFIG}`,
        { headers: getAuthHeaders() },
      );

      return response.data; // ✅ axios uses .data
    } catch (error) {
      console.error("Error fetching client config:", error);
      throw error;
    }
  },

  // Barcode search for invoice page scanning
  searchByBarcode: async (barcode) => {
    const response = await axios.get(
      `${API_URL}/stock/barcode/${encodeURIComponent(barcode)}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  // All stock history
  getAllStockHistory: async () => {
    const response = await axios.get(
      `${API_URL}/stock/history/all`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  // Billing status
  getBillingStatus: async () => {
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.BILLING_STATUS}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  // Accept Terms & Conditions
  acceptTerms: async (signedBy, version) => {
    const response = await axios.post(
      `${API_URL}/notification/billing/tc/accept`,
      { signedBy, version },
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  // Admin APIs
  adminGetClients: async () => {
    const response = await axios.get(`${API_URL}/notification/admin/clients`, { headers: getAuthHeaders() });
    return response.data;
  },

  adminUpdateClient: async (clientId, updates) => {
    const response = await axios.put(`${API_URL}/notification/admin/clients/${clientId}`, updates, { headers: getAuthHeaders() });
    return response.data;
  },

  adminDeleteClient: async (clientId) => {
    const response = await axios.delete(`${API_URL}/notification/admin/clients/${clientId}`, { headers: getAuthHeaders() });
    return response.data;
  },

  adminGetFieldConfig: async (clientCode) => {
    const response = await axios.get(`${API_URL}/notification/admin/field-config/${clientCode}`, { headers: getAuthHeaders() });
    return response.data;
  },

  adminSaveFieldConfig: async (data) => {
    const response = await axios.post(`${API_URL}/notification/admin/field-config`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  adminBulkSaveFieldConfig: async (data) => {
    const response = await axios.post(`${API_URL}/notification/admin/field-config/bulk`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  adminGetDashboardWidgets: async (clientCode) => {
    const response = await axios.get(`${API_URL}/notification/admin/dashboard-widgets/${clientCode}`, { headers: getAuthHeaders() });
    return response.data;
  },

  adminSaveDashboardWidget: async (data) => {
    const response = await axios.post(`${API_URL}/notification/admin/dashboard-widgets`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  adminInitDashboardWidgets: async (clientCode) => {
    const response = await axios.post(`${API_URL}/notification/admin/dashboard-widgets/init/${clientCode}`, {}, { headers: getAuthHeaders() });
    return response.data;
  },

  adminGetDashboardSettings: async (clientCode) => {
    const response = await axios.get(`${API_URL}/notification/admin/dashboard-settings/${clientCode}`, { headers: getAuthHeaders() });
    return response.data;
  },

  adminSaveDashboardSetting: async (clientCode, data) => {
    const response = await axios.post(`${API_URL}/notification/admin/dashboard-settings?clientCode=${clientCode}`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  // Dashboard widget config for current logged-in client
  getDashboardWidgetConfig: async () => {
    const response = await axios.get(`${API_URL}/notification/dashboard/widgets`, { headers: getAuthHeaders() });
    return response.data;
  },

  // Dashboard settings (lowStockThreshold, expiryAlertDays, stockSearchDefaultLoad)
  getDashboardSettings: async () => {
    const response = await axios.get(`${API_URL}/notification/dashboard/settings`, { headers: getAuthHeaders() });
    return response.data;
  },

  getStockSearchDefaultLoad: async () => {
    const response = await axios.get(
      `${API_URL}/notification/dashboard/settings/stock-search-default-load`,
      { headers: getAuthHeaders() },
    );
    return response.data; // { settingKey, value: true|false }
  },

  // Per-client chatbot feature flag for the logged-in client.
  getChatbotEnabled: async () => {
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.CHATBOT_ENABLED}`,
      { headers: getAuthHeaders() },
    );
    return response.data; // { settingKey: "chatbotEnabled", value: true|false }
  },

  // Per-client GST Report feature flag.
  getGstReportEnabled: async () => {
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.GST_REPORT_ENABLED}`,
      { headers: getAuthHeaders() },
    );
    return response.data; // { settingKey: "gstReportEnabled", value: true|false }
  },

  // Per-client WhatsApp bill sharing feature flag.
  getWhatsAppBillEnabled: async () => {
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.WHATSAPP_BILL_ENABLED}`,
      { headers: getAuthHeaders() },
    );
    return response.data; // { settingKey: "whatsappBillEnabled", value: true|false }
  },

  saveDashboardSetting: async (data) => {
    const response = await axios.post(`${API_URL}/notification/dashboard/settings`, data, { headers: getAuthHeaders() });
    return response.data;
  },

  // Product master APIs
  getAllProducts: async () => {
    const response = await axios.get(
      `${API_URL}/product/all`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  createProductMaster: async (productData) => {
    const response = await axios.post(
      `${API_URL}/product/create`,
      productData,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  updateProduct: async (productId, productData) => {
    const response = await axios.put(
      `${API_URL}/product/update?productId=${productId}`,
      productData,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },
};

// Order Services
export const orderService = {
  createOrder: async (order) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.CREATE_ORDER}`,
      order,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  searchOrders: async (params) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.SEARCH_ORDERS}`,
      params,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  searchProductSalesReport: async (params) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.PRODUCT_SALES_REPORT}`,
      params,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  searchPendingPayments: async (params) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.PENDING_PAYMENTS}`,
      params,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  collectPayment: async (data) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.COLLECT_PAYMENT}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  downloadPaymentReport: async (type, params) => {
    const endpoint =
      type === "pdf"
        ? API_ENDPOINTS.PAYMENT_REPORT_PDF
        : API_ENDPOINTS.PAYMENT_REPORT_EXCEL;
    const response = await axios.post(`${API_URL}${endpoint}`, params, {
      headers: getAuthHeaders(),
      responseType: "blob",
    });
    const ext = type === "pdf" ? "pdf" : "xlsx";
    const status = params.paymentStatus === "F" ? "completed" : "pending";
    const url = URL.createObjectURL(response.data);
    const link = document.createElement("a");
    link.href = url;
    link.download = `payments_${status}_${new Date().toISOString().slice(0, 10)}.${ext}`;
    link.click();
    URL.revokeObjectURL(url);
  },

  searchCustomers: async (params) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.SEARCH_CUSTOMERS}`,
      params,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  getCustomerProfile: async (mobile) => {
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.CUSTOMER_PROFILE(mobile)}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  getCustomerOrders: async (mobile, page = 0, size = 15) => {
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.CUSTOMER_ORDERS(mobile)}?page=${page}&size=${size}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  processReturn: async (data) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.RETURN_ORDER}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  getReturnsByOrderId: async (orderId) => {
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.GET_RETURNS(orderId)}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  getOrderDetail: async (orderId) => {
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.GET_ORDER_DETAIL(orderId)}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  cancelOrder: async (orderId) => {
    const response = await axios.patch(
      `${API_URL}${API_ENDPOINTS.CANCEL_ORDER(orderId)}`,
      {},
      { headers: getAuthHeaders() },
    );
    return response.data;
  },
};

// Distributor Services
export const distributorService = {
  // ── Distributor CRUD ──────────────────────────────────────────────────────
  create: async (data) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.CREATE_DISTRIBUTOR}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  searchDistributors: async ({
    distributorName = "",
    contactNumber = "",
    status = null,
    page = 0,
    size = 10,
  } = {}) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.SEARCH_DISTRIBUTORS}`,
      {
        distributorName: distributorName || null,
        contactNumber: contactNumber || null,
        status: status || null,
        page,
        size,
      },
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  getByMobile: async (mobile) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.GET_DISTRIBUTOR_BY_MOBILE}`,
      { contactNumber: mobile },
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  updateDistributor: async (data) => {
    const response = await axios.put(
      `${API_URL}${API_ENDPOINTS.UPDATE_DISTRIBUTOR}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  deleteDistributor: async (distributorId) => {
    const response = await axios.delete(
      `${API_URL}${API_ENDPOINTS.DELETE_DISTRIBUTOR}`,
      { headers: getAuthHeaders(), data: { distributorId } },
    );
    return response.data;
  },

  // ── Distributor Bill CRUD ─────────────────────────────────────────────────
  createBill: async (data) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.CREATE_DISTRIBUTOR_BILL}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  searchBills: async ({
    billNumber = "",
    distributorName = "",
    contactNumber = "",
    fromDate = "",
    toDate = "",
    page = 0,
    size = 10,
  } = {}) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.SEARCH_DISTRIBUTOR_BILLS}`,
      {
        billNumber: billNumber || null,
        distributorName: distributorName || null,
        contactNumber: contactNumber || null,
        fromDate: fromDate || null,
        toDate: toDate || null,
        page,
        size,
      },
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  updateBill: async (data) => {
    const response = await axios.put(
      `${API_URL}${API_ENDPOINTS.UPDATE_DISTRIBUTOR_BILL}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  deleteBill: async (billId, distributorId) => {
    const response = await axios.request({
      method: "DELETE",
      url: `${API_URL}${API_ENDPOINTS.DELETE_DISTRIBUTOR_BILL}`,
      headers: getAuthHeaders(),
      data: { billId: Number(billId), distributorId: Number(distributorId) },
    });
    return response.data;
  },

  // ── Combined ──────────────────────────────────────────────────────────────
  createBillWithDistributor: async (data) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.CREATE_BILL_WITH_DISTRIBUTOR}`,
      data,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  getDistributorWithBill: async (distributorId, billId) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.GET_DISTRIBUTOR_WITH_BILL}`,
      { distributorId, billId },
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  getBillByNumber: async (billNumber, distributorId) => {
    const response = await axios.post(
      `${API_URL}/audit/v1/distributors/bills/by-number`,
      { billNumber, distributorId },
      { headers: getAuthHeaders() },
    );
    return response.data;
  },
};

// AI Chatbot Service
export const chatService = {
  // Sends a message to the AI assistant. The axios interceptors attach the
  // Bearer JWT automatically and handle 401 refresh, so this stays minimal.
  sendMessage: async (message, conversationId, confirmed = false) => {
    const body = { message, confirmed };
    if (conversationId) body.conversation_id = conversationId;
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.AI_CHAT}`,
      body,
      { headers: getAuthHeaders() },
    );
    return response.data; // { conversation_id, answer, pending_action }
  },

  // Uploads a purchase receipt (image/PDF/Excel/CSV) for AI extraction.
  // Returns a structured draft { distributor, rows, notes } — it does NOT
  // create anything. The Stock Create page resolves + saves the data.
  extractStock: async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.AI_EXTRACT_STOCK}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          "X-Authenticated-User": localStorage.getItem(STORAGE_KEYS.USERNAME),
        },
        // Extraction can take longer than a normal call (model + file).
        timeout: 60000,
      },
    );
    return response.data; // { distributor:{...}, rows:[...], notes:[...] }
  },
};

// Offers Service (Notification Service). Reuses axios interceptors for JWT.
export const offerService = {
  // Per-client offer channel: { settingKey:"offerChannel", value:"off"|"wa_me"|... }
  getOfferChannel: async () => {
    const response = await axios.get(`${API_URL}${API_ENDPOINTS.OFFER_CHANNEL}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  createOffer: async (payload) => {
    const response = await axios.post(`${API_URL}${API_ENDPOINTS.OFFERS}`, payload, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  updateOffer: async (offerId, payload) => {
    const response = await axios.put(`${API_URL}${API_ENDPOINTS.OFFERS}/${offerId}`, payload, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  getOffer: async (offerId) => {
    const response = await axios.get(`${API_URL}${API_ENDPOINTS.OFFERS}/${offerId}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  searchOffers: async ({ title = "", page = 0, size = 10 } = {}) => {
    const params = new URLSearchParams();
    if (title && title.trim()) params.append("title", title.trim());
    params.append("page", page);
    params.append("size", size);
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.OFFERS}?${params.toString()}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  // Records the send server-side and returns per-recipient wa.me links to open.
  sendOffer: async (payload) => {
    const response = await axios.post(`${API_URL}${API_ENDPOINTS.OFFERS_SEND}`, payload, {
      headers: {
        ...getAuthHeaders(),
        // Display-only company name for the {shopName} placeholder.
        // Same source as the page header, so {shopName} in the offer matches
        // the client name shown across the app.
        "X-Company-Name": localStorage.getItem("companyName") || "Jaya Shree",
      },
    });
    return response.data;
  },

  // Distinct offer titles for the current client (for filter dropdowns).
  getOfferTitles: async () => {
    const response = await axios.get(`${API_URL}${API_ENDPOINTS.OFFERS_TITLES}`, {
      headers: getAuthHeaders(),
    });
    return response.data; // string[]
  },

  // Sellable items (product + size) with max honorable discount, for the
  // offer item picker. Sourced from Stock-Service via Notification-Service.
  // All filters are optional and applied server-side (AND-combined LIKE).
  getOfferItems: async ({ name, medicineType, manufacturer } = {}) => {
    const params = new URLSearchParams();
    if (name && name.trim()) params.append("name", name.trim());
    if (medicineType && medicineType.trim()) params.append("medicineType", medicineType.trim());
    if (manufacturer && manufacturer.trim()) params.append("manufacturer", manufacturer.trim());
    const qs = params.toString();
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.OFFERS_ITEMS}${qs ? `?${qs}` : ""}`,
      { headers: getAuthHeaders() },
    );
    return response.data; // [{ productName, medicineType, manufacturer, size, maxDiscount }]
  },

  // Distinct filter options for the offer-item picker (product names, types,
  // manufacturers). Each list is narrowed server-side by the current selections
  // so the pickers stay mutually consistent.
  getOfferItemFilters: async ({ name, medicineType, manufacturer } = {}) => {
    const params = new URLSearchParams();
    if (name && name.trim()) params.append("name", name.trim());
    if (medicineType && medicineType.trim()) params.append("medicineType", medicineType.trim());
    if (manufacturer && manufacturer.trim()) params.append("manufacturer", manufacturer.trim());
    const qs = params.toString();
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.OFFERS_ITEM_FILTERS}${qs ? `?${qs}` : ""}`,
      { headers: getAuthHeaders() },
    );
    return response.data; // { productNames, medicineTypes, manufacturers }
  },

  getSendHistory: async ({ offerId = null, page = 0, size = 10 } = {}) => {
    const params = new URLSearchParams();
    if (offerId) params.append("offerId", offerId);
    params.append("page", page);
    params.append("size", size);
    const response = await axios.get(
      `${API_URL}${API_ENDPOINTS.OFFERS_HISTORY}?${params.toString()}`,
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  // ── WhatsApp Cloud API onboarding (per client) ──────────────────────────
  getWhatsAppConfig: async () => {
    const response = await axios.get(`${API_URL}${API_ENDPOINTS.WHATSAPP_CONFIG}`, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  connectWhatsApp: async ({ code, wabaId, phoneNumberId }) => {
    const response = await axios.post(
      `${API_URL}${API_ENDPOINTS.WHATSAPP_CONNECT}`,
      { code, wabaId, phoneNumberId },
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  setWhatsAppTemplate: async ({ templateName, language }) => {
    const response = await axios.put(
      `${API_URL}${API_ENDPOINTS.WHATSAPP_TEMPLATE}`,
      { templateName, language },
      { headers: getAuthHeaders() },
    );
    return response.data;
  },

  disconnectWhatsApp: async () => {
    await axios.delete(`${API_URL}${API_ENDPOINTS.WHATSAPP_DISCONNECT}`, {
      headers: getAuthHeaders(),
    });
  },
};
